import React, { useState, useEffect, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
    Typography,
    Grid,
    IconButton,
    Button,
    Box,
} from '@mui/material';
import {
    LoadingBar,
    Select,
    DateInput,
    Input,
    BreadCrumbs,
    Documents,
    PrimaryButton,
    Tabs,
    CommonModal,
    Error404,
    ActionBox
} from '../Common';
import Routes from '../../routes';
import LocationHeader from './LocationViewHeader';
import { hotjar } from 'react-hotjar';
import { Pill } from "../Common/Pill"
import ServiceEvents from "./ServiceEvents";
import Providers from './Providers';
import { Alias, AddAlias } from './Alias';
import { Inventory } from './Inventory';
import SubLocation from './Sublocation';
import LocationRegistration from './LocationRegistration';
import { trakrefBlue } from '../../color_utils';
import { trakrefFontFamily, trakrefFontFamilyBold, downloadFile } from '../../utils';
import UserInfo from '../../UserInfo';
import Enums from '../../enums';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QuickReferenceAll, MoveLocation } from "../../Icons/Icons"
import { determineColorStatus } from "../../color_utils";
import WorkOrderTable from "./WorkOrderTable";
import DeactivateLocation from './DeactivateLocation';
import ReactivateLocation from './ReactivateLocation';
import '../Common/Common.css';
import "./LocationView.css";
import useNavigate from '../../hooks/useNavigate';
import useBreadCrumbs from '../../hooks/useBreadCrumbs';
import useViewport from '../../hooks/useViewport';
import useTabs from "../../context/CustomTab/useTabs";

const LocationView = ({
    root,
    history = {},
    history: {
        location: {
            state: {
                from
            } = {}
        } = {}
    } = {},
    location = {},
    match: {
        params: { id = '' } = {},
    },
}) => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [canEdit, setCanEdit] = useState(false);
    const [refreshgrid, setRefreshgrid] = useState(true);
    const [registrationTypes, setRegistrationTypes] = useState([]);
    const [showAddAlias, setShowAddAlias] = useState(false);
    const [showDeactivate, setShowDeactivate] = useState(false);
    const [isEditLocReg, setIsEditLocReg] = useState(false);
    const [isDeleteModalOn, setIsDeleteModalOn] = useState(false);
    const [editLocRegData, setEditLocRegData] = useState({});
    const [EditRegType, setEditRegType] = useState(null);
    const [EditRegStart, setEditRegStart] = useState(null);
    const [EditRegEnd, setEditRegEnd] = useState(null);
    const [EditRegFacilityID, setEditRegFacilityID] = useState(null);
    const [locRegFile, setLocRegFile] = useState([]);
    const [actionBoxItems, setActionBoxItems] = useState([]);
    const [showReactivate, setShowReactivate] = useState(false);
    const fileInputRef = useRef();
    const [savingReg, setSavingReg] = useState(false);

    const [modalStates, setModalStates] = useState({
        locationID: id,
    });

    const [errors, setErrors] = useState({});
    const [refreshData, setRefreshData] = useState(crypto.randomUUID());

    const { handleTabChange, activeTab, handleSecondTabChange, secondaryActiveTab } = useTabs(true);
    // US 16739: reactive so mobile exclusions (e.g. Documents Add-CTA) update on resize.
    const { isMobile } = useViewport();
    const navigate = useNavigate({
        pathname: 'LocationView'
    });
    const breadcrumbs = useBreadCrumbs({
        path: { id: 'LocationView', text: `Location #${id}`, link: history.location.pathname }
    });

    const {
        location: {
            locationName = '',
        } = {}
    } = data;

    const setState = (key, value) => (
        setModalStates((prevState) => ({
            ...prevState,
            [key]: value
        }))
    );

    const resetModalStates = () => (
        setModalStates({
            locationID: id,
        })
    );

    const IsNativeAdmin = (instanceId) => {
        return (UserInfo.isTenantAdministrator() || UserInfo.isSuperTenantAdmin() || UserInfo.isSystemAdministrator()) && instanceId === UserInfo.instanceId;
    };

    const fetchData = async () => {
        const response = await fetch(Routes.Location.Details(id));
        const locationRegistration = await fetch(Routes.Location.DropDowns());
        if (response.status === 404 || !response.ok) {
            setData({ location: { enabled: false } });
            return setLoading(false);
        }

        const json = await response.json();
        const { registrationTypes: dropdowns = [] } = await locationRegistration.json();

        if (!json || !json.location) {
            setData({ location: { enabled: false } });
            return setLoading(false);
        }

        const edit = UserInfo.canServiceLocation(json.location.id, json.location.instanceId) === true &&
            UserInfo.getInstance(json.location.instanceId).accountTypeId !== Enums.AccountTypes.ContractorLite &&
            UserInfo.getInstance(json.location.instanceId).accountTypeId !== Enums.AccountTypes.Contractor &&
            !UserInfo.isReadOnly()
            ;

        setData(json);
        setRegistrationTypes(dropdowns);
        setCanEdit(edit);

        var items = [];

        const locationStatus = json.location.locationStatus;
        const locOperational = Enums.LocationStatus.Operational;

        if (edit && json.location.locationStatus === Enums.LocationStatus.Operational &&
            (UserInfo.isTenantAdministrator() ||
                UserInfo.isSystemAdministrator() ||
                UserInfo.isTechnicianManager() ||
                UserInfo.isSuperTenantAdmin()
            )) {

            items.push({
                Icon: AddCircleOutlineIcon,
                title: "Create Inventory",
                onClick: () => navigate.push(`/AddInventory`, {
                    state:  { parentAssetLocationID: modalStates.locationID }
                })
            });
        }

        if (
            edit
            && json.location.locationStatus === Enums.LocationStatus.Operational
            && (
                UserInfo.isSystemAdministrator()
                || (
                    (IsNativeAdmin(json.location.instanceId) || UserInfo.isRegionalAdministrator()) &&
                    !UserInfo.isNativeAccountServiceProvider()
                   ) 
            )
        ) {
            items.push(
                {
                    Icon: EditIcon,
                    title: "Add Location Registration",
                    onClick: () => setState("open", true),
                },
                {
                    Icon: ContentCopyIcon,
                    title: "Create Location Alias",
                    onClick: () => AddAliasClicked()
                }
            );
        }

        if (!UserInfo.isReadOnly() && 
            (
                (
                    (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator()) && 
                    (UserInfo.isNativeUser() && UserInfo.isSystemOwner())
                )   ||
                UserInfo.isSystemAdministrator()
            )
            ) 
         {
            items.push({
                Icon: QuickReferenceAll,
                title: "Create Audit",
                onClick: () => navigate.push(id + '/CreateAudit')
            });
        }

        if (
            edit
            && UserInfo.getInstance(json.location.instanceId).enabled === true
            && IsNativeAdmin(json.location.instanceId) === true
            && (
                json.location.locationType === Enums.LocationType.ServiceLocation ||
                (json.location.locationType === Enums.LocationType.Warehouse ||
                    (json.location.locationType === Enums.LocationType.TechnicianTruck &&
                        json.hasActiveAssets === false &&
                        json.hasLocationCylinders === false))
            )
        ) {
            items.push({
                Icon: locationStatus === locOperational ? CloseIcon : MoveLocation,
                title: locationStatus === locOperational ? 'Deactivate Location' : 'Reactivate Location',
                onClick: locationStatus === locOperational ? deactivateClicked : reactivateClicked
            });
        }

        setActionBoxItems(items);
        setLoading(false);
    };

    const editLocation = () => navigate.push(`/EditLocation/${id}`);

    const UploadAllFiles = async (LocRegID) => {
        const uploadPromises = locRegFile.map((file) => UploadFile(file, LocRegID));
        try {
            // Wait for all upload promises to complete then refresh
            await Promise.allSettled(uploadPromises);
            setRefreshData(crypto.randomUUID());
        } catch (error) {
            console.error("Error Uploading File in Location Registration", error);
        }
    };

    const UploadFile = (file, LocRegID) => {
    return new Promise((resolve, reject) => {
        const fileExt = `.${file.name.split('.').pop()}`;
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = async () => {
            const files = [{
                fileName: file.name,
                fileExt,
                fileTypeId: 17,
                refId: LocRegID,
                contentType: file.type,
                notes: "",
                fileBytes: reader.result,
            }];

            const response = await fetch(Routes.File.UploadFile(), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    files
                }),
            });

            const form_data = new FormData();

            for (const key in data) {
                form_data.append(key, data[key]);
            };

            const options = {
                method: 'POST',
                body: form_data,
            };

            if (response.status === 200) {
                toast.success('File Uploaded Successfully', { duration: 5000 });
                resolve();
            } else {
                toast.error('Error Uploading File', { duration: 5000 });
                reject(new Error('Error Uploading File'));
            }
        };
    });
    };

    const createLocationRegistration = async () => {

        setSavingReg(true);

        var hasErrors = false;
        const errors = {};
        setErrors(errors);

        if (
            !modalStates.locationRegistrationTypeID
            || !modalStates.registrationStartDate
            || !modalStates.registrationEndDate
        ) {
            errors.locationRegistrationTypeID = !modalStates.locationRegistrationTypeID;
            errors.registrationStartDate = !modalStates.registrationStartDate;
            errors.registrationEndDate = !modalStates.registrationEndDate;

            hasErrors = true
        }

        if (!/^[a-zA-Z0-9 -]+$/.test(modalStates.facilityID)) {
            errors.facilityID = true;
            hasErrors = true
        }

        if (hasErrors) {
            setErrors(errors);
            setSavingReg(false);
            return;
        }

        const registrationURL = Routes.Location.AddRegistration();
        const registrationOptions = {
            method: 'POST',
            body: JSON.stringify(modalStates),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        };
        fetch(registrationURL, registrationOptions)
            .then(async (response) => {
                const LocID = await response.json()
                console.log(LocID)
                if (locRegFile != null && locRegFile.length >= 1) {
                    UploadAllFiles(LocID);
                }
                toast.success('The location has been registered successfully.', { duration: 5000 });
                setLocRegFile([]);
                setState("open", false);
                setSavingReg(false);
                resetModalStates();
            })
            .finally(() => {
                if (locRegFile === null || locRegFile.length === 0) {
                    setRefreshData(crypto.randomUUID());
                }}
            )
            .catch(err => {
                toast.error('Error Creating Location Registration', { duration: 5000 });
                console.log('Error creating Location Registration', err);
                setSavingReg(false);
            });
    };

    const EditLocationRegistrationModal = (data) => {
        setLocRegFile([])
        console.log(registrationTypes)
        console.log(data)
        setEditLocRegData(data)
        setEditRegType(data.registrationTypeID)
        setEditRegStart(data.startDate)
        setEditRegEnd(data.endDate)
        setEditRegFacilityID(data.facilityID)
        setIsEditLocReg(true)
    };

    const DeleteLocationRegistrationModal = (data) => {
        setEditLocRegData(data);
        setIsDeleteModalOn(true);
    };

    const deleteLocationRegistration = () => {
        const registrationDeleteURL = Routes.Location.DeleteRegistration(editLocRegData.registrationID);
        const registrationOptions = {
            method: 'POST',
            body: JSON.stringify({}),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        };
        fetch(registrationDeleteURL, registrationOptions)
            .then(() => {
                toast.success('The location registration has been successfully deleted.', { duration: 5000 });
                resetEditModal()
                resetModalStates();
            })
            .finally(() => setRefreshData(crypto.randomUUID()))
            .catch(err => {
                toast.error('Error Deleting Location Registration', { duration: 5000 });
                console.log('Error creating Location Registration', err);
            });
    };

    const resetEditModal = () => {
        setIsEditLocReg(false);
        setIsDeleteModalOn(false);
        setEditLocRegData({});
        setEditRegType(null);
        setEditRegStart(null);
        setLocRegFile(null);
        setEditRegEnd(null);
        setEditRegFacilityID(null);
    };

    const EditLocationRegistration = () => {

        let EditData = {}
        EditData.RegistrationStartDate = EditRegStart;
        EditData.RegistrationEndDate = EditRegEnd;
        EditData.LocationRegistrationTypeId = EditRegType
        EditData.FacilityID = EditRegFacilityID


        setSavingReg(true);

        var hasErrors = false;
        const errors = {};
        setErrors(errors);

        if (
            !EditRegType
            || !EditRegStart
            || !EditRegEnd
        ) {
            errors.EditRegType = !EditRegType;
            errors.EditRegStart = !EditRegStart;
            errors.EditRegEnd = !EditRegEnd;

            hasErrors = true
        }

        if (!/^[a-zA-Z0-9 -]+$/.test(EditRegFacilityID)) {
            errors.EditRegFacilityID = true;
            hasErrors = true;
        }

        if (hasErrors) {
            setErrors(errors);
            setSavingReg(false);
            return;
        }

        const registrationURL = Routes.Location.EditRegistration(editLocRegData.registrationID);
        const registrationOptions = {
            method: 'POST',
            body: JSON.stringify(EditData),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        };
        fetch(registrationURL, registrationOptions)
            .then(() => {
                if (locRegFile != null && locRegFile.length >= 1) {
                    UploadAllFiles(editLocRegData.registrationID)
                }
                toast.success('Location Registration Updated Successfully', { duration: 5000 });
                resetEditModal()
                resetModalStates();
                setSavingReg(false);
            })
            .finally(() => {
                if (locRegFile === null || locRegFile.length === 0) {
                    setRefreshData(crypto.randomUUID());
                }
            })
            .catch(err => {
                toast.error('Error Creating Location Registration', { duration: 5000 });
                console.log('Error creating Location Registration', err);
                setSavingReg(false);
            });
    };

    const handleFileUpdate = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLocRegFile((prev) => [...prev, file]);
        }
    };

    const removeUpload = (e) => {
        setLocRegFile(locRegFile.filter((obj) => obj.name != e.name))
    };

    useEffect(() => {
        fetchData();
        hotjar.stateChange(document.URL + '#Service Events');
    }, [id, showDeactivate, showReactivate]);

    const AddAliasClicked = () => setShowAddAlias(true);

    const closeAddAlias = () => setShowAddAlias(false);

    const saveAddAlias = () => {
        setShowAddAlias(false);
        setRefreshgrid(!refreshgrid);
    };

    const deactivateClicked = () => setShowDeactivate(true);

    const closeDeactivate = () => setShowDeactivate(false);

    const saveDeactivate = () => {
        setShowDeactivate(false);
        setRefreshgrid(!refreshgrid);
    };

    // Reactivate 
    const reactivateClicked = () => setShowReactivate(true);

    const closeReactivate = () => setShowReactivate(false);

    const saveReactivate = () => {
        setShowReactivate(false);
        setRefreshgrid(!refreshgrid);
    };

    // US 16739 (QA): download the registration attachments directly instead of
    // the View-Attachments browser modal (not responsive, exposed Delete).
    // Bug 16855: _blank anchors to the blob SAS URL strand the installed PWA on
    // the blob origin — stream each file through the API and save it instead.
    const DownloadLocationFiles = async (id) => {
        try {
            const response = await fetch(Routes.Location.GetLocationFile(id));
            const res = await response.json();
            for (const file of (res || [])) {
                await downloadFile(file);
            }
        }
        catch {
            toast.error('Error Downloading Attachments', { duration: 5000 });
        }
    };

    const tabMemo = useMemo(() => {
        const tabs = {
            labels: [],
            children: []
        }

        tabs.labels.push("Service Events");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    <ServiceEvents id={id} />
                </Grid>
            </Grid>
        );

        tabs.labels.push("Work Orders");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    <WorkOrderTable id={id} />
                </Grid>
            </Grid>
        );

        if (UserInfo.isSystemAdministrator() || UserInfo.isTenantAdministrator() || UserInfo.isSuperTenantAdmin()) {
            tabs.labels.push("Service Providers");
            tabs.children.push(
                <Grid
                    container
                    style={{ marginBottom: '2%' }}
                >
                    <Grid item xs={12} lg={12} mb={1} pt={2}>
                        <Providers id={id} />
                    </Grid>
                </Grid>
            );
        }

        tabs.labels.push("Inventory");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    <Inventory 
                        id={id} 
                        currentTab={secondaryActiveTab}
                        handleTabChange={handleSecondTabChange} 
                    />
                </Grid>
            </Grid>
        );

        tabs.labels.push("Sublocations");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    <SubLocation id={id} canEdit={canEdit} />
                </Grid>
            </Grid>
        );

        tabs.labels.push("Documents");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    {/* US 16739: hide "Add Document +" on the mobile breakpoint (view-only). */}
                    <Documents id={id} canEdit={canEdit} fileType={3} showAddButton={!isMobile} hideActions={isMobile}/>
                </Grid>
            </Grid>
        );

        tabs.labels.push("Aliases");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    <Alias id={id} canEdit={canEdit} />
                </Grid>
            </Grid>
        );

        tabs.labels.push("Registrations");
        tabs.children.push(
            <Grid
                container
                style={{ marginBottom: '2%' }}
            >
                <Grid item xs={12} lg={12} mb={1} pt={2}>
                    <LocationRegistration
                        id={id}
                        refreshData={refreshData}
                        onEdit={(row) => EditLocationRegistrationModal(row)}
                        onDocumentClick={(row) => DownloadLocationFiles(row.registrationID)}
                        onDelete={(row) => DeleteLocationRegistrationModal(row)}
                    />
                </Grid>
            </Grid>
        );

        return tabs;
    }, [id, refreshData, isMobile])

    if (loading) {
        return <LoadingBar />;
    }

    if (data.location.enabled === false) {
        return <Error404/>;
    }

    // US 16739: on the mobile breakpoint the Location Detail actions are Edit
    // (below) + Create Inventory only; the remaining detail actions (Add
    // Registration, Create Alias, Create Audit, Deactivate) are desktop/tablet.
    const showEdit = canEdit && data.location.locationStatus === Enums.LocationStatus.Operational &&
        (UserInfo.isSystemAdministrator() || (
            (UserInfo.isTenantAdministrator() || UserInfo.isTechnicianManager() || UserInfo.isSuperTenantAdmin())
            && !UserInfo.isNativeAccountServiceProvider()
        ));
    const pwaActionItems = actionBoxItems.filter(item => item.title === 'Create Inventory');

    return (
        <>
            <BreadCrumbs
                path={breadcrumbs.getSortedBreadCrumbs()}
                sx={{ display: { xs: 'none', lg: 'flex' } }}
            />
            <Grid
                container
                direction="row"
                alignItems={{ xs: 'start', md: 'center' }}
                sx={{ marginBottom: '1%', pb: { xs: 2, md: 0 }, pt: { xs: 3, md: 0 } }}
            >
                {/* US 16739 (QA): xs sizing keeps the X and the name on one line
                    on mobile — same pattern as the Inventory detail header. */}
                <Grid item xs={1} md={'auto'}>
                    <IconButton aria-label="close" onClick={() => navigate.goBack("/Locations", from)}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={11} md={'auto'}>
                    <Typography
                        variant="h5"
                        style={{ color: '#1A173B' }}
                    >
                        {`Location #${id} - ${locationName}`}
                    </Typography>
                </Grid>
                <Grid item sx={{ marginLeft: '0.5%', display: { xs: 'none', md: 'flex' } }}>
                    <Pill
                        text={data.location.locationStatusName}
                        type={data.location.locationStatusName}
                        bgColor={determineColorStatus(data.location.locationStatusName)}
                    />
                </Grid>
                {showEdit && (
                    <Grid item sx={{ marginLeft: 'auto', display: { xs: 'none', md: 'block' } }}>
                        <PrimaryButton
                            buttonText='Edit'
                            onClick={editLocation}
                        />
                    </Grid>
                )}
            </Grid>
            {/* US 16739: mobile-only status pill + Edit, above the detail card */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: '16px',
                    px: 1,
                }}
            >
                <Pill
                    text={data.location.locationStatusName}
                    type={data.location.locationStatusName}
                    bgColor={determineColorStatus(data.location.locationStatusName)}
                />
                {showEdit && (
                    <PrimaryButton buttonText='Edit' onClick={editLocation} />
                )}
            </Box>
            {/* US 16739: mobile Create Inventory action, grouped under the header
                (kept clear of the tab strip). */}
            {pwaActionItems.length > 0 && (
                <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1, pb: 2 }}>
                    <ActionBox items={pwaActionItems} />
                </Box>
            )}

            <LocationHeader
                data={data}
                canEdit={canEdit}
                actionBoxItems={actionBoxItems}
            />

            <div className="p-0 mt-3 col-md-12 f-jb">
                <Tabs
                    scrollable
                    activeTab={activeTab}
                    onhandleChange={handleTabChange}
                    labels={tabMemo.labels}
                    tabElements={tabMemo.children}
                    tabCustomStyle={{ ml: { xs: 0, lg: "25px" } }}
                    tabPanelCustomStyle={{ ml: { xs: 0, lg: "8px" } }}
                />
            </div>

            <CommonModal
                open={isEditLocReg}
                close={() => setIsEditLocReg(false)}
                header="Edit Location Registration"
            >
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={12} style={{ marginTop: '2%' }} align='center'>
                        <Select
                            required
                            value={EditRegType != undefined ? EditRegType : null}
                            label="Registration Type"
                            width={65}
                            options={registrationTypes}
                            onChange={e => setEditRegType(e.target.value)}
                            error={errors.EditRegType}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <DateInput
                            allowPast
                            allowFuture
                            date={EditRegStart}
                            onChange={new_date => setEditRegStart(new_date)}
                            label="Start Date"
                            width={65}
                            error={errors.EditRegStart}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <DateInput
                            allowPast
                            allowFuture
                            date={EditRegEnd}
                            onChange={new_date => setEditRegEnd(new_date)}
                            minDate={EditRegStart}
                            label="End Date"
                            width={65}
                            error={errors.EditRegEnd}
                        />
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Input
                            sx={{ minWidth: 100 }}
                            value={EditRegFacilityID}
                            onChange={e => setEditRegFacilityID(e.target.value)}
                            label="Facility ID"
                            variant="outlined"
                            fullWidth
                            error={errors.EditRegFacilityID}
                            helperText={"Invalid Facility Id"}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <input type='file' onChange={handleFileUpdate} ref={fileInputRef} style={{ display: 'none' }} />
                        {
                            locRegFile != null && locRegFile.map((file) => (
                                <Grid xs={8} lg={8} mb={1.5}>
                                    <div className="FileNameContainer">
                                        <p className="fileText">{file.name}</p>
                                        <img className="clickable" onClick={() => removeUpload(file)} src={"/images/ic_cross.svg"} />
                                    </div>
                                </Grid>
                            ))
                        }
                        <Grid>
                            <Typography className="clickable" onClick={() => fileInputRef.current.click()} sx={{ padding: '0% 0%', color: '#008299', fontSize: 17, fontWeight: 400 }}>
                                ATTACH DOCUMENT(S)
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <Box mb={2} sx={{ width: '75%' }}>
                            <PrimaryButton
                                fullWidth
                                size='large'
                                variant='contained'
                                onClick={EditLocationRegistration}
                                buttonText={"Update Location Registration"}
                                disabled={savingReg}
                            >
                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </CommonModal>

            <CommonModal
                open={isDeleteModalOn}
                close={() => setIsDeleteModalOn(false)}
                header="Are you sure you want to permanently delete this registration?"
                renderCloseIcon={false}
                headerTopMargin={8}
                headerFontWeight={500}
            >
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={12} align='center'>
                        <Box mb={2} sx={{ width: '65%' }}>
                            <Button
                                fullWidth
                                variant='contained'
                                size='large'
                                onClick={deleteLocationRegistration}
                                style={{ backgroundColor: trakrefBlue }}
                            >
                                <Typography
                                    variant='h6'
                                    sx={{
                                        padding: '2% 0%',
                                        color: '#fff',
                                        fontSize: '16px',
                                        '&.MuiTypography-root': { fontFamily: trakrefFontFamilyBold }
                                    }}
                                >
                                    YES, DELETE
                                </Typography>
                            </Button>
                        </Box>
                        <Box mb={2} sx={{ width: '65%' }}>
                            <Button
                                fullWidth
                                size='large'
                                variant='outlined'
                                onClick={resetEditModal}
                                sx={{ borderColor: trakrefBlue }}
                            >
                                <Typography
                                    variant='h6'
                                    sx={{
                                        padding: '2% 0%',
                                        color: trakrefBlue,
                                        fontSize: '16px',
                                        '&.MuiTypography-root': { fontFamily: trakrefFontFamilyBold }
                                    }}
                                >
                                    Cancel
                                </Typography>
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CommonModal>

            <CommonModal
                open={modalStates.open}
                close={() => setState("open", false)}
                header="Add Location Registration"
            >
                <Grid container spacing={3} align='center'>
                    <Grid item xs={12} lg={12} style={{ marginTop: '2%' }} align='center'>
                        <Select
                            required
                            value={modalStates.locationRegistrationTypeID}
                            label="Registration Type"
                            width={65}
                            options={registrationTypes}
                            onChange={e => setState("locationRegistrationTypeID", e.target.value)}
                            error={errors.locationRegistrationTypeID}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <DateInput
                            allowPast
                            allowFuture
                            date={modalStates.registrationStartDate}
                            onChange={new_date => setState("registrationStartDate", new_date)}
                            label="Start Date *"
                            width={65}
                            error={errors.registrationStartDate}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <DateInput
                            allowPast
                            allowFuture
                            minDate={modalStates.registrationStartDate}
                            date={modalStates.registrationEndDate}
                            onChange={new_date => setState("registrationEndDate", new_date)}
                            label="End Date *"
                            width={65}
                            error={errors.registrationEndDate}
                        />
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Input
                            value={modalStates.facilityID}
                            onChange={e => setState("facilityID", e.target.value)}
                            label="Facility ID"
                            variant="outlined"
                            fullWidth
                            error={errors.facilityID}
                            helperText={"Invalid Facility Id"}
                        />
                    </Grid>
                    <Grid item xs={12} lg={12} sx={{ maxWidth: "65%", marginLeft: 0 }} align='center'>
                        <input type='file' onChange={handleFileUpdate} ref={fileInputRef} style={{ display: 'none' }} />
                        {locRegFile != null && locRegFile.map((file) => (
                            <Grid xs={8} lg={8} mb={1.5}>
                                <div className="FileNameContainer">
                                    <p className="fileText">{file.name}</p>
                                    <img className="clickable" onClick={() => removeUpload(file)} src={"/images/ic_cross.svg"} />
                                </div>
                            </Grid>
                        ))}
                        <Grid>
                            <Typography
                                className="clickable"
                                onClick={() => fileInputRef.current.click()}
                                sx={{ padding: '0% 0%', color: '#008299', fontSize: 17, fontWeight: 400 }}
                            >
                                ATTACH DOCUMENT(S)
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} lg={12} align='center'>
                        <Box mb={2} sx={{ width: '65%' }}>
                            <PrimaryButton
                                fullWidth
                                size='large'
                                variant='contained'
                                disabled={savingReg}
                                onClick={createLocationRegistration}
                                buttonText={"Add Registration"}
                            >

                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </CommonModal>

            <CommonModal open={showAddAlias} close={closeAddAlias}>
                <AddAlias id={id} saveCallback={saveAddAlias} />
            </CommonModal>

            {showDeactivate && (
                <DeactivateLocation
                    id={id} name={locationName}
                    saveCallback={saveDeactivate}
                    open={showDeactivate}
                    close={() => closeDeactivate}
                />
            )}

            {showReactivate && (
                <ReactivateLocation
                    id={id}
                    name={locationName}
                    saveCallback={saveReactivate}
                    open={showReactivate}
                    close={() => closeReactivate}
                />
            )}
        </>
    );
};

export default LocationView;
